import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import financeAiService from '@/services/financeAi';
import financeCategorizationEventsService from '@/services/financeCategorizationEvents';
import financeRulesService from '@/services/financeRules';
import financeVendorsService from '@/services/financeVendors';
import { FetchApiIds } from '@/types';
import { FxCategorizationRule, FxVendor, FxCategory } from '@/types/api/finance';

/** Minimum number of override events before auto-learning triggers */
const AUTO_LEARN_THRESHOLD = 20;

type LearnResult = {
  created: number;
  deactivated: number;
  boosted: number;
  vendorUpdates: number;
};

type UseCategorizationLearningParams = {
  vendors: FxVendor[];
  categories: FxCategory[];
  rules: FxCategorizationRule[];
};

export default function useCategorizationLearning({
  vendors,
  categories,
  rules,
}: UseCategorizationLearningParams): {
  isLearning: boolean;
  learnResult: string | null;
  triggerLearning: () => Promise<void>;
  autoLearnIfNeeded: () => Promise<void>;
  learnInBackground: () => void;
} {
  const queryClient = useQueryClient();
  const [isLearning, setIsLearning] = useState(false);
  const [learnResult, setLearnResult] = useState<string | null>(null);
  const learningInProgress = useRef(false);

  const runLearning = useCallback(async (): Promise<LearnResult> => {
    // Gather data for learning
    const [recentOverrides, ruleEffectiveness, staleRules] = await Promise.all([
      financeCategorizationEventsService.getRecentOverrides(50),
      financeCategorizationEventsService.getRuleEffectiveness(),
      financeRulesService.getStaleRules(),
    ]);

    // Build vendor affinity for overridden vendors
    const vendorIds = [
      ...new Set(recentOverrides.map((e) => e.newVendorId).filter(Boolean)),
    ] as string[];
    const vendorAffinities = await Promise.all(
      vendorIds.slice(0, 20).map(async (vId) => {
        const affinity = await financeCategorizationEventsService.getVendorAffinity(vId);
        const vendor = vendors.find((v: FxVendor) => v.id === vId);
        return {
          vendorName: vendor?.displayName || vendor?.name || 'Unknown',
          categories: affinity.map((a) => ({ name: a.categoryName, percentage: a.percentage })),
        };
      })
    );

    const overridesForAi = recentOverrides.map((e) => ({
      description: e.description ?? '',
      amount: e.amount ?? 0,
      prevVendor: vendors.find((v: FxVendor) => v.id === e.prevVendorId)?.name ?? 'Unknown',
      prevCategory:
        categories.find((c: FxCategory) => c.id === e.prevCategoryId)?.name ?? 'Unknown',
      newVendor: vendors.find((v: FxVendor) => v.id === e.newVendorId)?.name ?? 'Unknown',
      newCategory: categories.find((c: FxCategory) => c.id === e.newCategoryId)?.name ?? 'Unknown',
    }));

    const existingRulesForAi = rules.map((r: FxCategorizationRule) => ({
      matchPattern: r.matchPattern,
      matchType: r.matchType,
      vendorName: r.vendor?.name ?? 'Unknown',
      hitCount: r.hitCount,
      overrideCount: r.overrideCount,
    }));

    const staleRulesForAi = staleRules.map((r) => {
      const eff = ruleEffectiveness.find((e) => e.ruleId === r.id);
      return { id: r.id, matchPattern: r.matchPattern, accuracy: eff?.accuracy ?? 100 };
    });

    // Call AI
    const aiResult = await financeAiService.learnFromFeedback({
      recentOverrides: overridesForAi,
      vendorAffinities,
      existingRules: existingRulesForAi,
      staleRules: staleRulesForAi,
    });

    let created = 0;
    let deactivated = 0;
    let vendorUpdates = 0;

    // Create new rules
    for (const newRule of aiResult.newRules ?? []) {
      const vendor = vendors.find(
        (v: FxVendor) =>
          v.name.toLowerCase() === newRule.vendorName.toLowerCase() ||
          v.displayName?.toLowerCase() === newRule.vendorName.toLowerCase()
      );
      const category = categories.find(
        (c: FxCategory) => c.name.toLowerCase() === newRule.categoryName.toLowerCase()
      );

      try {
        const existing = await financeRulesService.findByPattern(newRule.matchPattern);
        if ((existing.data as any)?.length > 0) continue;
      } catch {
        continue;
      }

      await financeRulesService.create({
        name: `AI: ${newRule.vendorName} - ${newRule.matchPattern}`,
        matchType: newRule.matchType,
        matchPattern: newRule.matchPattern,
        vendorId: vendor?.id,
        categoryId: category?.id,
        source: 'ai_learned',
        priority: 50,
      });
      created += 1;
    }

    // Deactivate bad rules
    for (const ruleId of aiResult.deactivateRuleIds ?? []) {
      await financeRulesService.update(ruleId, { isActive: false });
      deactivated += 1;
    }

    // Prune & optimize
    const pruneResult = await financeRulesService.pruneAndOptimize();
    deactivated += pruneResult.deactivated;

    // Update vendor default categories
    for (const update of aiResult.vendorCategoryUpdates ?? []) {
      const vendor = vendors.find(
        (v: FxVendor) =>
          v.name.toLowerCase() === update.vendorName.toLowerCase() ||
          v.displayName?.toLowerCase() === update.vendorName.toLowerCase()
      );
      const category = categories.find(
        (c: FxCategory) => c.name.toLowerCase() === update.categoryName.toLowerCase()
      );
      if (vendor && category) {
        await financeVendorsService.update(vendor.id, { categoryId: category.id });
        vendorUpdates += 1;
      }
    }

    queryClient.invalidateQueries(FetchApiIds.FX_CATEGORIZATION_RULES);
    queryClient.invalidateQueries(FetchApiIds.FX_VENDORS);
    queryClient.invalidateQueries('fx-active-rules');

    return { created, deactivated, boosted: pruneResult.boosted, vendorUpdates };
  }, [vendors, categories, rules, queryClient]);

  /** Manual trigger — shows result in learnResult state */
  const triggerLearning = useCallback(async () => {
    if (learningInProgress.current) return;
    learningInProgress.current = true;
    setIsLearning(true);
    setLearnResult(null);
    try {
      const result = await runLearning();
      const parts: string[] = [];
      if (result.created > 0)
        parts.push(`Created ${result.created} rule${result.created !== 1 ? 's' : ''}`);
      if (result.deactivated > 0) parts.push(`deactivated ${result.deactivated}`);
      if (result.boosted > 0) parts.push(`boosted ${result.boosted}`);
      if (result.vendorUpdates > 0)
        parts.push(
          `updated ${result.vendorUpdates} vendor default${result.vendorUpdates !== 1 ? 's' : ''}`
        );
      setLearnResult(
        parts.length > 0 ? `${parts.join(', ')}.` : 'No changes needed — system is up to date.'
      );
    } catch (err: any) {
      setLearnResult(`Error: ${err.message || 'Learning failed'}`);
    } finally {
      setIsLearning(false);
      learningInProgress.current = false;
    }
  }, [runLearning]);

  /** Silent auto-trigger — checks override count, runs in background if threshold met */
  const autoLearnIfNeeded = useCallback(async () => {
    if (learningInProgress.current) return;
    try {
      const count = await financeCategorizationEventsService.getOverrideCountThisMonth();
      // Only auto-trigger if enough overrides have accumulated
      // and count is a multiple of the threshold (so it doesn't re-trigger on every edit)
      if (count > 0 && count % AUTO_LEARN_THRESHOLD === 0) {
        learningInProgress.current = true;
        await runLearning();
        learningInProgress.current = false;
      }
    } catch {
      learningInProgress.current = false;
    }
  }, [runLearning]);

  /** Silent fire-and-forget — always runs (used after imports) */
  const learnInBackground = useCallback(() => {
    if (learningInProgress.current) return;
    learningInProgress.current = true;
    runLearning()
      .catch(() => {})
      .finally(() => {
        learningInProgress.current = false;
      });
  }, [runLearning]);

  return {
    isLearning,
    learnResult,
    triggerLearning,
    autoLearnIfNeeded,
    learnInBackground,
  };
}
